import {DescriptionOutlined} from '@mui/icons-material';
import {Box, Typography} from '@mui/material';
import {FC, useMemo} from 'react';
import {FormattedMessage} from 'react-intl';
import {Link} from 'react-router-dom';
import {ContractStatusEnum} from 'src/enums';
import {ContractDTO} from 'src/types';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {RecordActionBar} from 'src/components/mobile/RecordActionBar';
import {legacyActionsSx} from 'src/components/mobile/RecordActionBar/RecordActionBar.sx';
import {RecordIdentityCard} from 'src/components/mobile/RecordIdentityCard';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {
    badgeSx,
    cardSx,
    factLabelSx,
    factLinkSx,
    factNoteSx,
    factSx,
    factValueSx,
    factsSx,
    headerSx,
    statusPillSx,
    subtitleSx,
    titleSx,
} from './ContractIdentityCard.sx';
import {ContractIdentityCardProps} from './ContractIdentityCard.types';

const STATUS_TONE: Record<string, 'running' | 'pending' | 'over'> = {
    [ContractStatusEnum.OPERATION]: 'running',
    [ContractStatusEnum.INSTALLATION]: 'pending',
    [ContractStatusEnum.WAIT_FOR_SIGNATURE]: 'pending',
    [ContractStatusEnum.VALIDATION]: 'pending',
    [ContractStatusEnum.ARCHIVED]: 'over',
    [ContractStatusEnum.REJECTED]: 'over',
};

const asDate = (value?: number) => (value ? new Date(value).toLocaleDateString('fr-CH') : null);

/**
 * What this contract is, in one card.
 *
 * The three actions used to be in two places — one in the page header, two in a column under the
 * plan — so a reader looking for "archive" had to know it was not with the others. They are together
 * here, in the order they escalate: advance it, change it, end it.
 */
export const ContractIdentityCard: FC<ContractIdentityCardProps> = ({data, actions}) => {
    const signedAt = useMemo(() => asDate(data.signedAt) ?? asDate(data.created), [data]);
    const operationalAt = useMemo(() => asDate(data.operationalAt), [data]);
    const archivedAt = useMemo(() => asDate(data.archivedAt), [data]);

    const parking = data.user?.parkingAddress;
    const address = parking
        ? [parking.country, parking.city, parking.street, parking.houseNumber].filter(Boolean).join(', ')
        : null;
    const {isCompact} = useScreenTier();

    /** The contract's state, and the dates it changed state on. Drawn once, placed twice. */
    const statusPill = (
        <Box component="span" sx={statusPillSx(STATUS_TONE[data.status] ?? 'pending')}>
            <FormattedMessage id={data.status}/>
        </Box>
    );

    const timeline = (
        <>
            {signedAt && <><FormattedMessage id="signatureDate"/>{` ${signedAt}`}</>}
            {operationalAt && <>{' · '}<FormattedMessage id="operationChange"/>{` ${operationalAt}`}</>}
            {archivedAt && <>{' · '}<FormattedMessage id="archived"/>{` ${archivedAt}`}</>}
        </>
    );

    const clientName = `${data.user?.firstName ?? ''} ${data.user?.lastName ?? ''}`.trim() || '—';

    if (isCompact) {
        return (
            <>
                <RecordIdentityCard
                    icon={<DescriptionOutlined/>}
                    title={data.name}
                    subtitle={timeline}
                    status={statusPill}
                    facts={[
                        {
                            id: 'userName',
                            label: <FormattedMessage id="userName"/>,
                            value: (
                                <>
                                    {data.user?.id ? (
                                        <Typography
                                            component={Link}
                                            to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, String(data.user.id)])}
                                            sx={factLinkSx}
                                        >
                                            {clientName}
                                        </Typography>
                                    ) : '—'}
                                    {data.user?.phoneNumber && (
                                        <Typography sx={factNoteSx}>{data.user.phoneNumber}</Typography>
                                    )}
                                </>
                            ),
                        },
                        {
                            id: 'parkingLot',
                            label: <FormattedMessage id="parkingLot"/>,
                            value: (
                                <>
                                    {parking?.parkingPlace || '—'}
                                    {address && <Typography sx={factNoteSx}>{address}</Typography>}
                                </>
                            ),
                        },
                        {
                            id: 'signatureDate',
                            label: <FormattedMessage id="signatureDate"/>,
                            value: signedAt ?? '—',
                        },
                    ]}
                />
                {actions && (
                    <RecordActionBar>
                        {/* The page's own three, in the order they escalate: advance it, change it,
                            end it. Each keeps the tone it already had - "move to operation" filled,
                            "archive" in the brand. */}
                        <Box sx={legacyActionsSx}>{actions}</Box>
                    </RecordActionBar>
                )}
            </>
        );
    }

    return (
        <Box sx={cardSx}>
            <Box sx={headerSx}>
                <Box sx={badgeSx}>
                    <DescriptionOutlined/>
                </Box>
                <Box sx={{minWidth: 0}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap'}}>
                        <Typography sx={titleSx}>{data.name}</Typography>
                        {statusPill}
                    </Box>
                    <Typography sx={subtitleSx}>{timeline}</Typography>
                </Box>

                <Box sx={{flex: '1 1 auto'}}/>

                {actions}
            </Box>

            <Box sx={factsSx}>
                <Box sx={factSx}>
                    <Typography sx={factLabelSx}>
                        <FormattedMessage id="userName"/>
                    </Typography>
                    {/* A link, as the same name already is in every list. From a contract the client
                        is the obvious next step, and it was plain text. */}
                    {data.user?.id ? (
                        <Typography
                            component={Link}
                            to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, String(data.user.id)])}
                            sx={factLinkSx}
                        >
                            {`${data.user.firstName ?? ''} ${data.user.lastName ?? ''}`.trim() || '—'}
                        </Typography>
                    ) : (
                        <Typography sx={factValueSx}>—</Typography>
                    )}
                    {data.user?.phoneNumber && (
                        <Typography sx={factNoteSx}>{data.user.phoneNumber}</Typography>
                    )}
                </Box>

                <Box sx={factSx}>
                    <Typography sx={factLabelSx}>
                        <FormattedMessage id="parkingLot"/>
                    </Typography>
                    <Typography sx={factValueSx}>{parking?.parkingPlace || '—'}</Typography>
                    {address && <Typography sx={factNoteSx}>{address}</Typography>}
                </Box>

                <Box sx={factSx}>
                    <Typography sx={factLabelSx}>
                        <FormattedMessage id="signatureDate"/>
                    </Typography>
                    <Typography sx={factValueSx}>{signedAt ?? '—'}</Typography>
                    {operationalAt && (
                        <Typography sx={factNoteSx}>
                            <FormattedMessage id="operationChange"/>
                            {` ${operationalAt}`}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

